import {
  CNSPlanTemplateSummary,
  CNSSubscriptionStatus,
} from "@/models/cns";

export type BillingCheckoutKind = "subscription" | "topup";

const CHECKOUT_INTENT_KEY_PREFIX = "cns:billing-checkout-intent:";

interface BillingCheckoutIntentPayload {
  kind: BillingCheckoutKind;
  at: string;
}

/** Persists checkout kind before redirecting to Stripe (same-tab refresh keeps sessionStorage). */
export function setBillingCheckoutIntent(
  workspaceId: string,
  kind: BillingCheckoutKind
): void {
  if (typeof window === "undefined") return;
  const payload: BillingCheckoutIntentPayload = { kind, at: new Date().toISOString() };
  sessionStorage.setItem(
    `${CHECKOUT_INTENT_KEY_PREFIX}${workspaceId}`,
    JSON.stringify(payload)
  );
}

/** Reads and clears checkout intent for the workspace. */
export function consumeBillingCheckoutIntent(
  workspaceId: string
): BillingCheckoutKind | null {
  if (typeof window === "undefined") return null;
  const key = `${CHECKOUT_INTENT_KEY_PREFIX}${workspaceId}`;
  const raw = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as BillingCheckoutIntentPayload;
    if (parsed.kind === "subscription" || parsed.kind === "topup") {
      return parsed.kind;
    }
  } catch {
    // ignore malformed payload
  }
  return null;
}

export function isSubscriptionActive(status: CNSSubscriptionStatus | undefined): boolean {
  return status === "ACTIVE" || status === "TRIALING";
}

/** Backend portal needs a payment_provider_customer; not tied to ACTIVE-only. */
export function canOpenCustomerPortal(
  status: CNSSubscriptionStatus | undefined
): boolean {
  if (!status) return false;
  return (
    status !== "NONE" &&
    status !== "INCOMPLETE" &&
    status !== "INCOMPLETE_EXPIRED"
  );
}

export type SubscriptionStatusTone = "default" | "warning" | "muted" | "destructive";

export function subscriptionStatusDisplay(status: CNSSubscriptionStatus): {
  label: string;
  tone: SubscriptionStatusTone;
} {
  switch (status) {
    case "NONE":
      return { label: "No paid subscription", tone: "muted" };
    case "ACTIVE":
      return { label: "Active", tone: "default" };
    case "TRIALING":
      return { label: "Trial", tone: "default" };
    case "PAST_DUE":
      return { label: "Past due", tone: "warning" };
    case "CANCELED":
      return { label: "Canceled", tone: "muted" };
    case "UNPAID":
      return { label: "Unpaid", tone: "destructive" };
    case "PAUSED":
      return { label: "Paused", tone: "warning" };
    case "INCOMPLETE":
      return { label: "Incomplete checkout", tone: "warning" };
    case "INCOMPLETE_EXPIRED":
      return { label: "Checkout expired", tone: "muted" };
    default:
      return { label: status, tone: "muted" };
  }
}

/** Formats integer cents as a whole-unit currency string (no fractional digits). */
export function formatCents(cents: number, currency: string = "USD"): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

/** Formats a per-unit rate in cents, keeping two fractional digits (e.g. "$0.59"). */
export function formatUnitRate(cents: number, currency: string = "USD"): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * Highest-ranked self-serve plan: the priced, non-unlimited plan with the greatest {@code sortOrder}.
 * Used to resolve the "top plan" (e.g. staff activation) without coupling to a tier name.
 * Returns undefined when no purchasable plan exists in the list.
 */
export function highestPurchasablePlan(
  plans: CNSPlanTemplateSummary[]
): CNSPlanTemplateSummary | undefined {
  return plans
    .filter((plan) => plan.monthlyPriceCents > 0 && !plan.unlimitedPlan)
    .sort((a, b) => b.sortOrder - a.sortOrder)[0];
}

/**
 * Percentage saved by paying yearly instead of twelve monthly charges, rounded to a whole number.
 * Returns 0 when either price is missing or the yearly price offers no discount.
 */
export function yearlySavingsPct(plan: CNSPlanTemplateSummary): number {
  const monthlyAnnualized = plan.monthlyPriceCents * 12;
  if (monthlyAnnualized <= 0 || plan.yearlyPriceCents <= 0) return 0;
  const pct = (1 - plan.yearlyPriceCents / monthlyAnnualized) * 100;
  return pct > 0 ? Math.round(pct) : 0;
}

/** Relationship between the workspace's current plan and a candidate target plan. */
export type PlanChangeIntent = "current" | "upgrade" | "downgrade" | "activate-free";

/**
 * Classifies a candidate plan against the current assignment to drive consistent CTA copy.
 * Direction is derived from the authoritative {@code sortOrder}, not the tier label. Free targets
 * that are not the current plan resolve to {@code activate-free}.
 */
export function planChangeIntent(
  currentPlanId: string | undefined | null,
  currentSortOrder: number | undefined | null,
  target: CNSPlanTemplateSummary
): PlanChangeIntent {
  if (currentPlanId && target.id === currentPlanId) return "current";
  if (target.monthlyPriceCents === 0) return "activate-free";
  const current = currentSortOrder ?? -1;
  return target.sortOrder > current ? "upgrade" : "downgrade";
}

export interface PlanCtaCopy {
  label: string;
  variant: "default" | "outline";
  disabled: boolean;
}

/** Default CTA label and styling for a plan-change intent. */
export function planCtaCopy(intent: PlanChangeIntent): PlanCtaCopy {
  switch (intent) {
    case "current":
      return { label: "Current plan", variant: "outline", disabled: true };
    case "upgrade":
      return { label: "Upgrade", variant: "default", disabled: false };
    case "downgrade":
      return { label: "Switch plan", variant: "outline", disabled: false };
    case "activate-free":
      return { label: "Switch to free", variant: "outline", disabled: false };
  }
}
